import React, {
  useState,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import moment from 'moment';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';

import Select from '../../../../../../components/Select';
import useSessionStore from '../../../../../../hooks/useSessionStore';

import { DateFormat } from '../../../../../../utils/date';
import UserContract from '../../../../../../Model/UserContract';
import { getCurrentPeriodEnd } from '../../../../../../Model/Subscription';
import { ContractMinimumValue, getRemainingPaymentCount } from '../../../../../utils/userContract';

import texts from './texts.json';
import {
  StyledPaper,
  Container,
  OptionsContainer,
  AmendmentWarning,
} from './styles';

const cancelType = {
  IMMEDIATELY: 'IMMEDIATELY',
  END_OF_CURRENT_PERIOD: 'END_OF_CURRENT_PERIOD',
  END_OF_COMMITMENT: 'END_OF_COMMITMENT',
};

const CANCELLATION_REASON_OPTIONS = [
  'I don\'t have enough time to work out',
  'I\'m not getting the results I wanted',
  'App is buggy and a poor user experience',
  'I\'m switching to another fitness service',
  'I joined a gym or hired a personal trainer',
  'Health issues / injury',
  'I can\'t afford it right now',
  'Done with my commitment period',
  'Something Else',
].map((reason) => ({ value: reason, label: reason }));

const InternalType = {
  EOC: 'EOC',
  BC: 'BC',
  RE_ONBOARDING: 'Re-onboarding',
};

const INTERNAL_TYPE_OPTIONS = Object.values(InternalType)
  .map((option) => ({ value: option, label: option }));

const INTERNAL_REASON_OPTIONS = [
  'Finances',
  'Didn\'t like the program',
  'Switching program/trainer',
  'Ready to try on own',
  'App Issues',
  'Other',
].map((reason) => ({ value: reason, label: reason }));

const TYPES_REQUIRING_REASON = [InternalType.EOC, InternalType.BC];

const SubscriptionCancelDialog = ({
  isOpen,
  updateClientSubscription,
  cancelClientSubscription,
  onCancel,
  dialogTexts,
  subscription,
  userContractDoc,
  userDoc,
  hasAcceptedAmendment,
  isChallenge,
}) => {
  const { isCoachAssistant } = useSessionStore();
  const [selectedCancelType, setSelectedCancelType] = useState(cancelType.IMMEDIATELY);
  const [selectedReason, setSelectedReason] = useState('');
  const [customText, setCustomText] = useState('');
  const [selectedInternalType, setSelectedInternalType] = useState('');
  const [selectedSubReason, setSelectedSubReason] = useState('');

  const showSubReasonDropdown = isCoachAssistant && TYPES_REQUIRING_REASON.includes(selectedInternalType);

  const isConfirmDisabled = isCoachAssistant
    ? !selectedInternalType || (showSubReasonDropdown && !selectedSubReason)
    : !selectedReason;

  const cancelDate = useMemo(() => {
    // A challenge has no subscription to date a later cancellation from; it only ends now.
    if (!subscription) {
      return { [cancelType.IMMEDIATELY]: moment() };
    }
    const monthsToAdd = userContractDoc?.minSubscriptionMonths || ContractMinimumValue.MIN_SUBSCRIPTION_MONTHS;
    return {
      [cancelType.IMMEDIATELY]: moment(),
      [cancelType.END_OF_CURRENT_PERIOD]: moment.unix(getCurrentPeriodEnd(subscription)),
      [cancelType.END_OF_COMMITMENT]: userDoc.serviceStartAt
        ? moment(userDoc.serviceStartAt).add(monthsToAdd, 'months')
        : moment.unix(subscription.created).add(monthsToAdd, 'months'),
    };
  }, [
    subscription,
    userContractDoc,
    userDoc,
  ]);

  const remainingPayments = useMemo(
    () => (subscription ? getRemainingPaymentCount(userContractDoc, userDoc, subscription) : 0),
    [
      subscription,
      userContractDoc,
      userDoc,
    ],
  );

  const handleInternalTypeChange = useCallback((evt) => {
    setSelectedInternalType(evt.target.value);
    setSelectedSubReason('');
  }, []);

  const handleConfirm = useCallback(async () => {
    const trimmedCustomText = customText.trim();
    let cancellationReason;

    if (isCoachAssistant) {
      const reasonParts = [];
      if (selectedSubReason) reasonParts.push(selectedSubReason);
      if (trimmedCustomText) reasonParts.push(trimmedCustomText);
      cancellationReason = reasonParts.length > 0
        ? `${selectedInternalType} / ${reasonParts.join(', ')}`
        : selectedInternalType;
    } else {
      cancellationReason = trimmedCustomText
        ? `${selectedReason}, ${trimmedCustomText}`
        : selectedReason;
    }

    switch (selectedCancelType) {
      case cancelType.IMMEDIATELY:
        await cancelClientSubscription(cancellationReason, selectedCancelType);
        break;
      case cancelType.END_OF_CURRENT_PERIOD:
        await updateClientSubscription({
          cancel_at_period_end: true,
        }, cancellationReason, selectedCancelType);
        break;
      case cancelType.END_OF_COMMITMENT:
        await updateClientSubscription({
          cancel_at: cancelDate[cancelType.END_OF_COMMITMENT].unix(),
        }, cancellationReason, selectedCancelType);
        break;

      default:
        break;
    }
  }, [
    cancelClientSubscription,
    updateClientSubscription,
    selectedCancelType,
    selectedReason,
    customText,
    cancelDate,
    isCoachAssistant,
    selectedInternalType,
    selectedSubReason,
  ]);

  return (
    <Dialog
      open={isOpen}
      PaperComponent={StyledPaper}
    >
      <DialogTitle>
        {dialogTexts.title}
      </DialogTitle>
      <DialogContent>
        {texts.cancel}
        <OptionsContainer>
          <RadioGroup
            value={selectedCancelType}
            onChange={(evt) => setSelectedCancelType(evt.target.value)}
          >
            {(hasAcceptedAmendment || isChallenge ? [cancelType.IMMEDIATELY] : Object.keys(cancelType))
              .map((type) => cancelDate[type] >= cancelDate[cancelType.IMMEDIATELY]
              && (
                <FormControlLabel
                  key={type}
                  value={type}
                  control={<Radio />}
                  label={format(texts.cancelTypeLabel[type], {
                    date: cancelDate[type].format(DateFormat.MONTH_NAME_DATE_FORMAT),
                    remainingPayments: remainingPayments > 0 ? remainingPayments : 'No',
                  })}
                />
              ))}
          </RadioGroup>
        </OptionsContainer>
        {hasAcceptedAmendment && (
          <AmendmentWarning>
            {texts.amendmentNotice}
          </AmendmentWarning>
        )}
        {isCoachAssistant ? (
          <>
            <Container>
              <Select
                label={texts.internalType}
                value={selectedInternalType}
                onChange={handleInternalTypeChange}
                options={INTERNAL_TYPE_OPTIONS}
                error={!selectedInternalType}
                required
              />
              {!selectedInternalType && <FormHelperText error>{texts.required}</FormHelperText>}
            </Container>
            {showSubReasonDropdown && (
              <Container>
                <Select
                  label={texts.reason}
                  value={selectedSubReason}
                  onChange={(evt) => setSelectedSubReason(evt.target.value)}
                  options={INTERNAL_REASON_OPTIONS}
                  error={!selectedSubReason}
                  required
                />
                {!selectedSubReason && <FormHelperText error>{texts.required}</FormHelperText>}
              </Container>
            )}
          </>
        ) : (
          <Container>
            <Select
              label={texts.reason}
              value={selectedReason}
              onChange={(evt) => setSelectedReason(evt.target.value)}
              options={CANCELLATION_REASON_OPTIONS}
              error={!selectedReason}
              required
            />
            {!selectedReason && <FormHelperText error>{texts.required}</FormHelperText>}
          </Container>
        )}
        <Container>
          <TextField
            label={texts.additionalDetails}
            variant="outlined"
            onChange={(evt) => setCustomText(evt.target.value)}
            value={customText}
            fullWidth
            multiline
            rows={2}
          />
        </Container>
        <DialogContentText>
          {dialogTexts.content}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleConfirm}
          color="success"
          disabled={isConfirmDisabled}
        >
          {dialogTexts.confirm || texts.confirm}
        </Button>
        <Button
          onClick={onCancel}
          color="warning"
        >
          {dialogTexts.cancel || texts.cancel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SubscriptionCancelDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  dialogTexts: PropTypes.shape({
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    confirm: PropTypes.string,
    cancel: PropTypes.string,
  }).isRequired,
  // Absent for a challenge, which is a one-time payment with no subscription.
  subscription: PropTypes.object,
  cancelClientSubscription: PropTypes.func.isRequired,
  updateClientSubscription: PropTypes.func.isRequired,
  // The client as seen through the contract being cancelled, not the whole user doc.
  userDoc: PropTypes.object.isRequired,
  userContractDoc: PropTypes.instanceOf(UserContract),
  hasAcceptedAmendment: PropTypes.bool,
  // Ends the challenge now: the only cancel type offered, and no subscription is read.
  isChallenge: PropTypes.bool,
};

SubscriptionCancelDialog.defaultProps = {
  userContractDoc: null,
  hasAcceptedAmendment: false,
  isChallenge: false,
  subscription: null,
};

export default SubscriptionCancelDialog;
